import * as React from 'react'
import ReactSelect from 'react-select'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../../core/i18n'
import { BREAKPOINT_TABLET_SMALL } from '../../../../static/constants'
import * as constants from '../../../../static/constants'
import { InputText, InputTextArea } from '../../../atoms'
import { ConnectionVariablesDescription } from 'components/atoms/ConnectionVariablesDescription'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService, utilService } from '../../../../core/services'
import * as interfaces from '../../../../static/interfaces'
import SidebarLayout from '../../_layouts/sidebarLayout'
import AgentLayoutEdit from '../_layouts/edit'

declare var window: interfaces.IWindow

interface ISelectOption {
  value: string
  label: string
}

const title = I18n.t('agent.edit', {
  model: I18n.t('connection_mail_template', { scope: 'activerecord.models' }),
})

const FIELDS = {
  title: 'title',
  mail_subject: 'mail_subject',
  mail_text: 'mail_text',
}

const createBlankItemOptions = (blankItems: { [key: string]: string }): ISelectOption[] => {
  return Object.entries(blankItems).map(([key]) => ({
    value: key,
    label: I18n.t(`connection.blank_item.${key}`),
  }))
}

const createTargetUserTypeOptions = (targetUserTypes: {
  [key: string]: string
}): ISelectOption[] => {
  return Object.entries(targetUserTypes).map(([key]) => ({
    value: key,
    label: I18n.t(`generic.${key}`),
  }))
}

const findOptionByValue = (
  options: ISelectOption[],
  value: string | undefined | null
): ISelectOption | null => {
  if (!value) {
    return null
  }
  return options.find(opt => opt.value === String(value)) || null
}

interface IProps {
  template: IJsonResponse
  user: IJsonResponse
  enumData: {
    blank_items: { [key: string]: string }
    target_user_types: { [key: string]: string }
  }
}

const AgentConnectionMailTemplateEdit: React.FC<IProps> = props => {
  const { data: template } = agentService.getDataFromJson(props.template)
  const { data: userData } = utilService.getDataFromJson(props.user)

  const blankItemOptions = createBlankItemOptions(props.enumData.blank_items)
  const targetUserTypeOptions = createTargetUserTypeOptions(props.enumData.target_user_types)

  const [selectedBlankItem, setSelectedBlankItem] = React.useState(
    findOptionByValue(blankItemOptions, template.blank_item) || blankItemOptions[0]
  )
  const [selectedTargetUserType, setSelectedTargetUserType] = React.useState(
    findOptionByValue(targetUserTypeOptions, template.target_user_type) || targetUserTypeOptions[1]
  )

  const handleSubmit = React.useCallback(
    async (initialValues, values) => {
      const { flush } = await agentService.updateConnectionMailTemplate(template.id, {
        ...values,
        blank_item: selectedBlankItem.value,
        target_user_type: selectedTargetUserType.value,
      })
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      location.href = '/agent/connection_mail_templates'
    },
    [selectedBlankItem, selectedTargetUserType, template.id]
  )
  const isSelectDisabled =
    template.template_pair_id !== null || selectedBlankItem.value === 'nothing'
  const FormContent = () => (
    <>
      <S.FormItemWrapper>
        <S.ReactSelectWrapper>
          <p className="ReactSelect_Title">Target User Type</p>
          <div className="ReactSelect_Main">
            <ReactSelect
              name="target_user_type"
              placeholder="Select User Type"
              required={true}
              options={targetUserTypeOptions}
              onChange={setSelectedTargetUserType}
              value={selectedTargetUserType}
              menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
              isDisabled={isSelectDisabled}
            />
          </div>
        </S.ReactSelectWrapper>
      </S.FormItemWrapper>
      <S.FormItemWrapper>
        <S.ReactSelectWrapper>
          <p className="ReactSelect_Title">Blank Item</p>
          <div className="ReactSelect_Main">
            <ReactSelect
              name="blank_item"
              placeholder="Select Blank Item"
              required={true}
              options={blankItemOptions}
              onChange={setSelectedBlankItem}
              value={selectedBlankItem}
              menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
              isDisabled={isSelectDisabled}
            />
          </div>
        </S.ReactSelectWrapper>
      </S.FormItemWrapper>
      <S.FormItemWrapper>
        <InputText
          required={true}
          name="title"
          defaultValue={template.title || ''}
          label={I18n.t('generic.name')}
        />
      </S.FormItemWrapper>
      <S.FormItemWrapper>
        <InputText
          required={true}
          name="mail_subject"
          defaultValue={template.mail_subject || ''}
          label={I18n.t('mail_template.subject')}
        />
      </S.FormItemWrapper>
      <S.FormItemWrapper>
        <InputTextArea
          required={true}
          name="mail_text"
          defaultValue={template.mail_text || ''}
          label={I18n.t('generic.text')}
        />
      </S.FormItemWrapper>
      {/* Connection Variables Description */}
      <S.FormItemWrapper>
        <S.VariableDescriptionWrapper>
          <ConnectionVariablesDescription />
        </S.VariableDescriptionWrapper>
      </S.FormItemWrapper>
    </>
  )

  return (
    <SidebarLayout user={userData}>
      <AgentLayoutEdit
        model="connection_mail_template"
        title={title}
        fields={FIELDS}
        hideAgentNav={true}
        indexLink="/agent/connection_mail_templates"
        formItems={<FormContent />}
        handleSubmit={handleSubmit}
      />
    </SidebarLayout>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}

S.FormItemWrapper = styled.div`
  margin-top: 24px;
`

S.ReactSelectWrapper = styled.div`
  display: flex;
  margin-top: 24px;
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
  .ReactSelect_Title {
    min-width: 180px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;
    margin-top: 8px;

    .InputText_Required {
      display: inline-block;
      border-radius: 3px;
      color: ${constants.COLORS.white};
      background-color: ${constants.COLORS.caution};
      vertical-align: middle;
      font-size: 11px;
      font-weight: normal;
      padding: 4px 6px;
      margin-left: 8px;
    }

    @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
      margin-bottom: 12px;
    }
  }
  .ReactSelect_Main {
    flex: 1;
    font-size: 15px;
  }
`

S.VariableDescriptionWrapper = styled.div`
  margin-top: 40px;
  padding-top: 16px;
  border-top: 2px solid ${constants.COLORS.divider2};
`

S.NoteText = styled.p`
  margin-top: 16px;
  padding: 12px;
  border-left: 4px solid ${constants.COLORS.info};
  background-color: ${constants.COLORS.infoBG};
  color: ${constants.COLORS.infoText};
  font-size: 13px;
`

export default AgentConnectionMailTemplateEdit
